import { useState } from "react";
import { resetPassword } from "aws-amplify/auth";
import { TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { z } from "zod";
import { usePostHog } from "posthog-js/react";
import { AuthEvent } from "../../../data/auth-events";
import { emailSchema } from "./validation-schemas";
import styles from "./claimable-authenticator-v2.module.scss";

const forgotPasswordSchema = z.object({
  email: emailSchema,
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

type ForgotPasswordFormProps = {
  onCodeSent: (email: string) => void;
  onBackToSignIn: () => void;
  prefilledEmail?: string;
};

const ForgotPasswordForm = ({
  onCodeSent,
  onBackToSignIn,
  prefilledEmail,
}: ForgotPasswordFormProps) => {
  const posthog = usePostHog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ForgotPasswordFormValues>({
    initialValues: {
      email: prefilledEmail || "",
    },
    validate: zod4Resolver(forgotPasswordSchema),
  });

  const handleSubmit = async (values: ForgotPasswordFormValues) => {
    posthog?.capture(AuthEvent.ForgotPasswordSubmit);
    setIsSubmitting(true);
    setError(null);

    try {
      await resetPassword({
        username: values.email,
      });

      posthog?.capture(AuthEvent.ForgotPasswordSuccess);
      onCodeSent(values.email);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";
      posthog?.capture(AuthEvent.ForgotPasswordError, { error: errorMessage });
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.forgotPasswordForm}>
      <img
        src="/images/logo/logo_black_large.svg"
        alt="Claimable"
        className={styles.formLogo}
      />
      <h1 className={styles.formTitle}>{"Reset Password"}</h1>

      <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form}>
        <TextInput
          label="Enter your email"
          placeholder="Enter your email"
          size="lg"
          value={form.values.email}
          onChange={(e) => form.setFieldValue("email", e.currentTarget.value)}
          onBlur={() => form.validateField("email")}
          error={form.errors.email}
          classNames={{
            input: styles.mantineInput,
            label: styles.inputLabel,
          }}
        />

        {error && <p className={styles.formError}>{error}</p>}

        <button
          type="submit"
          className={styles.primaryButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? "SENDING..." : "SEND CODE"}
        </button>
      </form>

      <div className={styles.switchAuthMode}>
        <button
          type="button"
          onClick={onBackToSignIn}
          className={styles.switchLink}
        >
          {"Back to Sign In"}
        </button>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
